import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { useAuthStore } from '@/store/authStore'
import { apiService } from '@/services/api'
import { Calendar, Mail, Lock, AlertCircle } from 'lucide-react'
import { isValidEmail, normalizeEmail } from '@/utils/validation'
import { CLE_RETOUR_CONNEXION } from '@/pages/ConnecteurAutoriser'

export default function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Valider l'email côté client
    if (!isValidEmail(username)) {
      setError('Veuillez saisir une adresse email valide')
      setLoading(false)
      return
    }

    try {
      // Normaliser l'email avant l'envoi
      const normalizedEmail = normalizeEmail(username)
      
      // Vrai appel API vers le backend avec username
      const response = await apiService.login({ username: normalizedEmail, password })
      
      if (response.success && response.data) {
        // Sauvegarder l'utilisateur et le token
        setAuth(response.data.user, response.data.token)
        // Retour à l'écran de consentement du connecteur IA s'il a envoyé ici.
        let retour: string | null = null
        try {
          retour = sessionStorage.getItem(CLE_RETOUR_CONNEXION)
          sessionStorage.removeItem(CLE_RETOUR_CONNEXION)
        } catch { /* navigation privée */ }
        navigate(retour?.startsWith('/connecteur/') ? retour : '/dashboard')
      } else {
        setError(response.error || 'Erreur de connexion')
      }
    } catch (error) {
      console.error('Erreur de connexion:', error)
      setError('Erreur de connexion au serveur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo/Icône */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center">
            <Calendar className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Titre et sous-titre */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">UP-CRM</h1>
          <p className="text-gray-600">Connectez-vous pour accéder à votre CRM</p>
        </div>

        {/* Formulaire */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Message d'erreur */}
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              {/* Champ Email */}
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="username"
                    type="email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="pl-10 h-12"
                    placeholder="votre@email.com"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Champ Mot de passe */}
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 h-12"
                    placeholder="Mot de passe"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Se souvenir de moi et Mot de passe oublié */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Se souvenir de moi
                  </label>
                </div>
                <div className="text-sm">
                  <a href="/forgot-password" className="text-blue-600 hover:text-blue-500">
                    Mot de passe oublié ?
                  </a>
                </div>
              </div>

              {/* Bouton de connexion */}
              <Button 
                type="submit" 
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg" 
                disabled={loading}
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>


            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}